import React, { useEffect } from 'react';
import queryString from 'query-string';
import { connect } from 'react-redux';
import { withNamespaces } from 'react-i18next';
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import clsx from 'clsx';
import { get } from 'lodash';
import { createStructuredSelector } from 'reselect';
import { isExperienceEditorActive } from '@sitecore-jss/sitecore-jss';
import Grid from '../../../components/molecules/Grid';
import AutoCompleteMUI from '../../../components/atoms/AutoComplete';
import { searchData } from './data';
import ButtonMUI from '../../../components/atoms/Button';
import useStyles from './style';
import fetchService from '../../../../services/FetchService';
import { canUseDOM } from '../../../utils/canUseDOM';
import { modifyApi } from '../../../../utils/urls';
import withErrorBoundary from '../../../utils/HOCs/withErrorBoundary';
import { updateSearchTermData } from '../GlobalSearchSubLayout/redux/actions';
import { pushAnaylyticsData } from '../../../../utils/analytics';
import { analyticsData } from '../../../../constants/Analytics/analytics-data';
import { searchTermData } from '../GlobalSearchSubLayout/redux/selectors';
import { COMPONENT_NAME } from './locators';

const SearchBar = (props) => {
  let {
    className,
    ctaClass,
    placeHolder,
    disableClearIcon,
    isGlobalSearchPage,
    updateSearchTermData,
    redirectLink,
    searchTermData,
    id,
    sitecoreContext,
    t: dictionary,
    appConfig = {},
  } = props;

  let { autoComplete } = searchData && searchData.fields;
  const classes = useStyles();
  const [autoCompleteListItems, setAutoCompleteListItems] = React.useState([]);
  const [selectedValue, setSelectedValue] = React.useState('');
  const [searchTermStateData, setSearchTermStateData] = React.useState(
    searchTermData.data
  );
  const language = get(sitecoreContext, 'language', '');
  const searchLabel = dictionary && dictionary('searchText');

  let isEditor;

  const previewMode =
    sitecoreContext &&
    sitecoreContext.pageState &&
    sitecoreContext.pageState.toLowerCase();

  if (isExperienceEditorActive() || previewMode === 'preview') {
    isEditor = true;
  } else {
    isEditor = false;
  }

  /*
   * remove this when key is available from sitecore
   */
  if (!redirectLink) {
    redirectLink = {
      href: '/en/global-search-result',
      text: '',
      anchor: '',
      linktype: 'internal',
      class: '',
      title: '',
      querystring: '',
    };
  }
  if (id) {
    autoComplete.fields.fieldName.value = id;
  }
  if (searchLabel) {
    autoComplete.fields.fieldLabel.value = searchLabel;
  }

  const setFieldValue = value => {
    setSelectedValue(value);
  };

  if (searchTermData.data !== searchTermStateData) {
    setFieldValue(searchTermData.data);
    setSearchTermStateData(searchTermData.data);
  }
  useEffect(() => {
    if (canUseDOM) {
      const searchString = queryString.parse(get(window, 'location.search'));
      if (Object.keys(searchString).length !== 0) {
        if (searchString && searchString.searchTerm) {
          setFieldValue(searchString.searchTerm);
          updateSearchTermData(searchString.searchTerm);
        }
      }
    }
  }, []);


  if (placeHolder && placeHolder.value) {
    autoComplete.fields.placeHolder.value = placeHolder.value;
  }

  const onSearchClick = value => {
    if (value || (selectedValue && selectedValue.length)) {
      const selectedTerm = value ? value : selectedValue;
      try {
        let analyticsDataToSend = {
          ...get(analyticsData, 'headerInteraction.searchClick'),
        };
        analyticsDataToSend.label = selectedTerm;
        pushAnaylyticsData(analyticsDataToSend);
      } catch (err) {
        console.error('analytics events error', err);
      }
      if (isGlobalSearchPage) {
        updateSearchTermData(selectedTerm);
        const query = queryString.stringify({ searchTerm: selectedTerm });
        if (canUseDOM) {
          window.history.replaceState(
            { turbolinks: {} },
            '',
            `${window.location.pathname}?${query}`
          );
        }
      } else {
        if (canUseDOM && redirectLink && redirectLink.href) {
          let url = `${window.location.origin}${redirectLink.href}?searchTerm=${selectedTerm}`;
          window.location.href = url;
        }
      }
    }
  };

  const onValueSelect = e => {
    setFieldValue(e.target.value);
    onSearchClick(e.target.value);
  };

  const onKeyDown = e => {
    if (e.keyCode === 13) {
      onSearchClick();
      setAutoCompleteListItems([]);
    }
  };

  const onInputChange = value => {
    setFieldValue(value);
    /*
     * add autosearch call
     */
    if (value && value.length >= 3) {
      const url = modifyApi(appConfig.ADVANCE_SEARCH_API, {
        searchTerm: value,
        language: language,
        limit: 6,
        isEditor: isEditor,
      });

      fetchService({ url }).then(res => {
        if (get(res, 'data.data.suggestions', []).length > 0) {
          let options = [];
          const apiData = res.data.data.suggestions;
          apiData &&
            apiData.forEach(value => {
              if (value.name) {
                options.push({ name: value.name });
              }
            });
          setAutoCompleteListItems(options);
        } else {
          setAutoCompleteListItems([]);
        }
      });
    } else {
      setAutoCompleteListItems([]);
    }
  };

  const onSearchButtonClick = () => {
    onSearchClick();
  };

  return (
    <Grid
      className={clsx(className ? className : classes.inputRoot)}
      data-locator={COMPONENT_NAME}
    >
      <Grid
        item
        className={clsx(
          classes.searchInput,
          disableClearIcon && 'removeBorder'
        )}
      >
        {autoComplete && (
          <AutoCompleteMUI
            data={autoComplete.fields}
            disableClearable={disableClearIcon}
            inputChange={onInputChange}
            listItems={autoCompleteListItems}
            onChange={onValueSelect}
            inputValue={selectedValue}
            onKeyDown={onKeyDown}
            isSearchBar={true}
            variant={'body1'}
            classNameInput={'searchBarInputStyle'}
          />
        )}
        <ButtonMUI
          component="span"
          buttonType="primary"
          hasBorder={false}
          size="small"
          onClick={onSearchButtonClick}
          srText={(redirectLink && redirectLink.text) || searchLabel}
          className={`${clsx(ctaClass ? ctaClass : classes.CTAButton)} field-btn`}
          animateSettings={{
            willAnimate: false,
          }}
        ></ButtonMUI>
      </Grid>
    </Grid>
  );
};

export const mapDispatchToProps = dispatch => {
  return {
    updateSearchTermData: params => dispatch(updateSearchTermData(params)),
  };
};

export const mapStateToProps = createStructuredSelector({
  searchTermData: searchTermData,
});

export default withErrorBoundary(
  withSitecoreContext()(
    withNamespaces()(connect(mapStateToProps, mapDispatchToProps)(SearchBar))
  )
);
